import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Form, Input, DatePicker, Upload, Checkbox, Radio, Select, Modal, message } from 'antd';
import { ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, ArrowLeftOutlined, DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ArrowButton from '../arrowButton';
import { countryCodes } from '../../utils/countryCodes';
import './index.css';
import './index.tablet.css';
import './index.mobile.css';

const FORM_STORAGE_KEY = 'applicationFormData';

// 提取区号的主要部分（处理 +1-268 这样的格式）
const getMainCode = (value) => {
  return value.split('-')[0];
};

// 提取区号的数字部分用于排序（字符串字典序）
const getCodeForSort = (code) => {
  // 去掉 + 号，只保留数字部分用于字符串比较
  return code.replace('+', '');
};

// 创建去重后的区号列表（只保留唯一的区号）
const uniqueCountryCodes = (() => {
  const codeMap = new Map();
  
  countryCodes.forEach((item) => {
    const mainCode = getMainCode(item.value);
    // 如果这个区号还没有被添加，或者当前项是更常见的国家（比如 +1 优先显示美国而不是其他小岛）
    if (!codeMap.has(mainCode)) {
      codeMap.set(mainCode, mainCode);
    }
  });
  
  // 转换为数组并按字符串字典序排序（按第一个数字、第二个数字...依次比较）
  return Array.from(codeMap.values()).sort((a, b) => {
    const codeA = getCodeForSort(a);
    const codeB = getCodeForSort(b);
    // 字符串比较：按字典序排序，逐位比较
    if (codeA < codeB) return -1;
    if (codeA > codeB) return 1;
    return 0;
  });
})();

function ApplicationFormModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showHeaderShadow, setShowHeaderShadow] = useState(false);
  const [showFooterShadow, setShowFooterShadow] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const contentRef = useRef(null);
  const overlayRef = useRef(null);

  // 保存表单数据到 sessionStorage
  const saveFormData = useCallback(() => {
    try {
      console.log('saveFormData called');
      // 获取所有表单字段的值
      const formValues = form.getFieldsValue();
      // 将日期对象转换为字符串以便存储，并排除文件字段（无法序列化）
      const dataToSave = { ...formValues };
      if (dataToSave.childBirthDate && dayjs.isDayjs(dataToSave.childBirthDate)) {
        dataToSave.childBirthDate = dataToSave.childBirthDate.toISOString();
      }
      // 排除文件上传字段，因为它们包含 File 对象无法序列化
      delete dataToSave.studentIdFile;
      delete dataToSave.enrollmentCertFile;
      delete dataToSave.birthCertFile;
      const dataString = JSON.stringify(dataToSave);
      sessionStorage.setItem(FORM_STORAGE_KEY, dataString);
      console.log('Form data saved:', dataToSave);
    } catch (error) {
      console.error('Failed to save form data:', error);
    }
  }, [form]);

  // 恢复表单数据
  useEffect(() => {
    if (isOpen && shouldRender) {
      // 延迟恢复，确保表单完全渲染
      const timer = setTimeout(() => {
        try {
          const savedData = sessionStorage.getItem(FORM_STORAGE_KEY);
          if (savedData) {
            const formData = JSON.parse(savedData);
            console.log('Restoring form data:', formData);
            // 处理日期字段（从字符串转换为 dayjs 对象）
            if (formData.childBirthDate) {
              formData.childBirthDate = dayjs(formData.childBirthDate);
            }
            // 如果没有保存的 countryCode，设置默认值
            if (!formData.countryCode) {
              formData.countryCode = '+852';
            } else if (formData.countryCode.includes('-')) {
              // 兼容旧格式：如果包含 '-'，只提取区号部分
              formData.countryCode = formData.countryCode.split('-')[0];
            }
            // 确保区号格式正确（以 + 开头）
            if (formData.countryCode && !formData.countryCode.startsWith('+')) {
              formData.countryCode = '+' + formData.countryCode;
            }
            form.setFieldsValue(formData);
            // 更新复选框状态
            setAgreeTerms(formData.agreeTerms || false);
            setAgreePrivacy(formData.agreePrivacy || false);
            // 恢复后立即保存一次，确保数据同步
            setTimeout(() => {
              saveFormData();
            }, 50);
          } else {
            // 如果没有保存的数据，设置默认的国家代码
            form.setFieldsValue({ countryCode: '+852' });
            // 重置复选框状态
            setAgreeTerms(false);
            setAgreePrivacy(false);
            console.log('No saved form data found');
          }
        } catch (error) {
          console.error('Failed to restore form data:', error);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender, form, saveFormData]);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // 重置验证错误提示状态，确保每次打开时都是隐藏的
      setShowValidationError(false);
      document.body.style.overflow = 'hidden';
      // 延迟添加打开类名，确保动画可以播放
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // 关闭时保存表单数据
      if (shouldRender) {
        saveFormData();
      }
      setIsAnimating(false);
      // 关闭时延迟卸载，让关闭动画可以播放
      const timer = setTimeout(() => {
        setShouldRender(false);
        // 重置复选框状态
        setAgreeTerms(false);
        setAgreePrivacy(false);
        // 重置提交尝试状态
        setHasAttemptedSubmit(false);
        // 重置验证错误提示状态
        setShowValidationError(false);
      }, 300); // 与 CSS 过渡时间一致
      document.body.style.overflow = 'unset';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, shouldRender, saveFormData]);

  // 监听滚动事件，控制边框阴影显示
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      
      // 滚动超过10px时显示header阴影
      setShowHeaderShadow(scrollTop > 10);
      
      // 距离底部超过10px时显示footer阴影
      setShowFooterShadow(scrollTop < scrollHeight - clientHeight - 10);
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      // 初始化时检查一次
      handleScroll();
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  // 移除点击空白处关闭功能
  // const handleBackdropClick = (e) => {
  //   if (e.target === e.currentTarget) {
  //     onClose();
  //   }
  // };

  // 处理表单校验失败
  const handleFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    setShowValidationError(true);
    // 标记已经尝试提交过，之后启用实时校验
    setHasAttemptedSubmit(true);
    // 如果有报错，滚动到顶部
    if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  // 提交表单数据的辅助函数
  const submitFormData = async (values) => {
    // 合并国家代码和电话号码
    // countryCode 现在直接就是区号格式（如 +852）
    const cleanCountryCode = values.countryCode || '+852';
    
    // 创建 FormData 对象用于文件上传
    const formData = new FormData();
    
    // 添加普通表单字段
    if (values.name) formData.append('name', values.name);
    if (values.email) formData.append('email', values.email);
    formData.append('phone', cleanCountryCode + (values.phone || ''));
    if (values.university) formData.append('university', values.university);
    if (values.researchProject) formData.append('researchProject', values.researchProject);
    if (values.bankName) formData.append('bankName', values.bankName);
    if (values.accountHolder) formData.append('accountHolder', values.accountHolder);
    if (values.accountNumber) formData.append('accountNumber', values.accountNumber);
    if (values.interviewWillingness) formData.append('interviewWillingness', values.interviewWillingness);
    formData.append('agreeTerms', values.agreeTerms ? 'true' : 'false');
    formData.append('agreePrivacy', values.agreePrivacy ? 'true' : 'false');
    
    // 处理日期字段 - 支持 dayjs 对象或字符串
    if (values.childBirthDate) {
      let dateStr = '';
      if (dayjs.isDayjs(values.childBirthDate)) {
        dateStr = values.childBirthDate.format('YYYY-MM-DD');
      } else if (typeof values.childBirthDate === 'string') {
        // 如果是字符串，转换为 dayjs 再格式化
        dateStr = dayjs(values.childBirthDate).format('YYYY-MM-DD');
      }
      if (dateStr) {
        formData.append('childBirthDate', dateStr);
      }
    }
    
    // 提取并添加文件
    // 辅助函数：获取文件对象
    const getFileObject = (fileItem) => {
      // 优先使用 originFileObj（如果它是真正的 File 对象）
      if (fileItem?.originFileObj instanceof File) {
        return fileItem.originFileObj;
      }
      // 如果 fileItem 本身就是 File 对象
      if (fileItem instanceof File) {
        return fileItem;
      }
      // 如果都不行，返回 null（文件可能已丢失，需要重新上传）
      return null;
    };
    
    // 学生证文件（支持多个文件）
    if (values.studentIdFile && values.studentIdFile.length > 0) {
      values.studentIdFile.forEach((fileItem, index) => {
        const studentIdFile = getFileObject(fileItem);
        if (studentIdFile) {
          formData.append(`studentIdFile[${index}]`, studentIdFile);
        } else {
          console.warn(`studentIdFile[${index}] is not a valid File object:`, fileItem);
        }
      });
    }
    
    // 在读证明文件（支持多个文件）
    if (values.enrollmentCertFile && values.enrollmentCertFile.length > 0) {
      values.enrollmentCertFile.forEach((fileItem, index) => {
        const enrollmentCertFile = getFileObject(fileItem);
        if (enrollmentCertFile) {
          formData.append(`enrollmentCertFile[${index}]`, enrollmentCertFile);
        } else {
          console.warn(`enrollmentCertFile[${index}] is not a valid File object:`, fileItem);
        }
      });
    }
    
    // 出生证明文件（支持多个文件）
    if (values.birthCertFile && values.birthCertFile.length > 0) {
      values.birthCertFile.forEach((fileItem, index) => {
        const birthCertFile = getFileObject(fileItem);
        if (birthCertFile) {
          formData.append(`birthCertFile[${index}]`, birthCertFile);
        } else {
          console.warn(`birthCertFile[${index}] is not a valid File object:`, fileItem);
        }
      });
    }
    
    // 原服务（奖学金申请），经 /api-origin 代理到旧后端
    const response = await fetch('/api-origin/application/submit', {
      method: 'POST',
      body: formData,
      // 注意：不要设置 Content-Type，让浏览器自动设置（包含 boundary）
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: '提交失败，请稍后重试' }));
      throw new Error(errorData.message || `提交失败: ${response.status}`);
    }
    
    return await response.json();
  };

  const handleSubmit = async (values) => {
    // 防止重复提交
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    // 提交时隐藏错误提示（因为会重新校验）
    setShowValidationError(false);
    
    // 保存表单值以便重试
    setPendingFormValues(values);
    
    try {
      // 调试：打印表单值
      console.log('Form values received:', values);
      
      const result = await submitFormData(values);
      console.log('Form submitted successfully:', result);
      
      // 提交成功后清除保存的表单数据
      sessionStorage.removeItem(FORM_STORAGE_KEY);
      form.resetFields();
      // 重置复选框状态
      setAgreeTerms(false);
      setAgreePrivacy(false);
      // 重置提交尝试状态
      setHasAttemptedSubmit(false);
      
      // 显示成功弹窗
      setShowSuccessModal(true);
      setIsSubmitting(false);
      
    } catch (error) {
      console.error('Failed to submit form:', error);
      // 显示失败弹窗
      setShowErrorModal(true);
      setIsSubmitting(false);
    }
  };

  // 处理成功弹窗确认
  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    // 关闭所有弹窗（包括主表单弹窗）
    onClose();
  };

  // 处理失败弹窗重试
  const handleErrorRetry = async () => {
    if (!pendingFormValues) return;
    
    setShowErrorModal(false);
    setIsSubmitting(true);
    
    try {
      const result = await submitFormData(pendingFormValues);
      console.log('Form submitted successfully after retry:', result);
      
      // 提交成功后清除保存的表单数据
      sessionStorage.removeItem(FORM_STORAGE_KEY);
      form.resetFields();
      // 重置复选框状态
      setAgreeTerms(false);
      setAgreePrivacy(false);
      // 重置提交尝试状态
      setHasAttemptedSubmit(false);
      
      // 显示成功弹窗
      setShowSuccessModal(true);
      setIsSubmitting(false);
    } catch (error) {
      console.error('Failed to submit form after retry:', error);
      // 再次显示失败弹窗
      setShowErrorModal(true);
      setIsSubmitting(false);
    }
  };

  // 处理失败弹窗返回
  const handleErrorBack = () => {
    setShowErrorModal(false);
    setPendingFormValues(null);
  };

  // 文件上传验证函数
  const beforeUpload = (file) => {
    const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type) ||
      /\.(jpg|jpeg|png|pdf)$/i.test(file.name);
    
    if (!isValidType) {
      // 这里可以显示错误提示，但由于使用了 customRequest，错误提示会在 Form.Item 的 validator 中显示
      return Upload.LIST_IGNORE;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      // 文件大小超过限制，显示错误提示
      message.error(t('applicationForm.file.tooLarge'));
      return Upload.LIST_IGNORE;
    }
    
    // 返回 true 允许文件添加到列表中
    return true;
  };

  // 自定义上传组件样式
  const customUploadButton = (uploadText1, uploadText2, uploadText3) => (
    <div className="application-form-upload-label">
      <img src="/image/上传-申请表单.png" alt="upload" className="application-form-upload-icon" style={{ width: '28px', height: '28px' }} />
      <span className="application-form-upload-text">{uploadText1}</span>
      <span className="application-form-upload-hint">{uploadText2}</span>
      <button type="button" className="application-form-upload-button">{uploadText3}</button>
    </div>
  );

  // 处理文件预览
  const handlePreview = (file) => {
    const fileObj = file.originFileObj || file;
    if (!fileObj) return;
    
    let previewUrl = file.url;
    if (!previewUrl && fileObj instanceof File) {
      previewUrl = URL.createObjectURL(fileObj);
    }
    
    // 获取文件类型
    let fileType = fileObj.type || '';
    if (!fileType && file.name) {
      const ext = file.name.toLowerCase().split('.').pop();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
        fileType = 'image/' + ext;
      } else if (ext === 'pdf') {
        fileType = 'application/pdf';
      }
    }
    
    setPreviewFile({
      file: fileObj,
      name: file.name || fileObj.name || t('applicationForm.file.unknown'),
      url: previewUrl,
      type: fileType
    });
    setPreviewVisible(true);
  };

  // 关闭预览
  const handlePreviewClose = () => {
    setPreviewVisible(false);
    // 清理创建的 URL
    if (previewFile?.url && previewFile.url.startsWith('blob:')) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
  };

  // 自定义文件列表项渲染
  const customItemRender = (originNode, file, fileList, actions) => {
    // 直接返回完整的列表项结构，确保删除按钮正确渲染
    return (
      <div className="ant-upload-list-item">
        <div className="ant-upload-list-item-info">
          <span className="anticon ant-upload-icon">
            <PaperClipOutlined />
          </span>
          <span className="ant-upload-list-item-name" title={file.name}>
            {file.name}
          </span>
        </div>
        <span className="ant-upload-list-item-actions application-form-upload-list-item-actions">
          <button
            type="button"
            className="application-form-preview-button"
            onClick={(e) => {
              e.stopPropagation();
              handlePreview(file);
            }}
            title={t('common.ariaPreview')}
          >
            <EyeOutlined />
          </button>
          <button
            type="button"
            className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-action"
            onClick={(e) => {
              e.stopPropagation();
              actions.remove();
            }}
            title={t('common.ariaDelete')}
          >
            <DeleteOutlined />
          </button>
        </span>
      </div>
    );
  };

  // 计算导航栏高度 (假设导航栏高度为80px)
  const navHeight = 80;
  const modalPadding = 60;
  const modalHeight = `calc(100vh - ${navHeight + modalPadding * 2}px)`;

  return (
    <div 
      ref={overlayRef}
      className={`application-form-modal-overlay ${isAnimating ? 'application-form-modal-overlay-open' : ''}`}
    >
      <div 
        className={`application-form-modal-container ${isAnimating ? 'application-form-modal-container-open' : ''}`}
        style={{ height: modalHeight }}
      >
        <button className="application-form-modal-close" onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>
        
        {/* 固定顶部的标题 */}
        <div className={`application-form-modal-header ${showHeaderShadow ? 'has-shadow' : ''}`}>
          <h2 className="application-form-modal-title">{t('applicationForm.title')}</h2>
          
        </div>

        <div className="application-form-modal-content" ref={contentRef}>
          <p className="application-form-modal-subtitle">{t('applicationForm.subtitle')}</p>
          {showValidationError && (
            <div className="application-form-warning">
              <ExclamationCircleOutlined className="application-form-warning-icon" />
              <span className="application-form-warning-text">{t('applicationForm.validationError')}</span>
            </div>
          )}

          <Form 
            form={form}
            className="application-form" 
            onValuesChange={(changedValues, allValues) => {
              saveFormData();
              console.log('changedValues', changedValues);
              // 监听两个复选框字段的变化，始终从 allValues 获取最新值
              if ('agreeTerms' in allValues) {
                setAgreeTerms(Boolean(allValues.agreeTerms));
              }
              if ('agreePrivacy' in allValues) {
                setAgreePrivacy(Boolean(allValues.agreePrivacy));
              }
              // 如果已经尝试提交过，检查表单是否有错误，如果没有错误则隐藏错误提示
              if (hasAttemptedSubmit) {
                const errors = form.getFieldsError();
                const hasErrors = errors.some(({ errors: fieldErrors }) => fieldErrors && fieldErrors.length > 0);
                if (!hasErrors && showValidationError) {
                  setShowValidationError(false);
                }
              }
            }}
            onFinish={handleSubmit}
            onFinishFailed={handleFinishFailed}
            layout="vertical"
            validateTrigger={hasAttemptedSubmit ? ['onBlur', 'onChange'] : []}
          >
            {/* 姓名 */}
            <Form.Item
              name="name"
              label={<span className="application-form-label">{t('applicationForm.fields.name.label')}</span>}
              rules={[{ required: true, message: t('applicationForm.fields.name.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.name.hint')}</p>}
            >
              <Input
                className="application-form-input"
                placeholder={t('applicationForm.fields.name.placeholder')}
              />
            </Form.Item>

            {/* 电邮地址 */}
            <Form.Item
              name="email"
              label={<span className="application-form-label">{t('applicationForm.fields.email.label')}</span>}
              rules={[
                { required: true, message: t('applicationForm.fields.email.required') },
                { 
                  type: 'email', 
                  message: t('applicationForm.fields.email.required')
                }
              ]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.email.hint')}</p>}
            >
              <Input
                type="email"
                className="application-form-input"
                placeholder={t('applicationForm.fields.email.placeholder')}
              />
            </Form.Item>

            {/* 电话号码 */}
            <Form.Item
              name="phone"
              label={<span className="application-form-label">{t('applicationForm.fields.phone.label')}</span>}
              className="application-form-field"
              required
              dependencies={['countryCode']}
              rules={[
                { required: true, message: t('applicationForm.fields.phone.required') },
                {
                  pattern: /^[0-9]+$/,
                  message: t('applicationForm.fields.phone.required')
                },
                {
                  min: 6,
                  message: t('applicationForm.fields.phone.required')
                },
                {
                  max: 15,
                  message: t('applicationForm.fields.phone.required')
                }
              ]}
              extra={<p className="application-form-hint">{t('applicationForm.fields.phone.hint')}</p>}
            >
              <Form.Item dependencies={['phone']} noStyle>
                {() => {
                  const phoneError = form.getFieldError('phone');
                  const hasPhoneError = phoneError && phoneError.length > 0;
                  return (
                    <Input.Group compact className="application-form-phone-group">
                      <Form.Item
                        name="countryCode"
                        noStyle
                        initialValue="+852"
                        rules={[{ required: true, message: '' }]}
                      >
                        <Select
                          className={`application-form-country-select ${hasPhoneError ? 'application-form-country-select-error' : ''} ${isCountrySelectOpen ? 'is-open' : ''}`}
                          style={{ paddingLeft: "16px" }}
                          status={hasPhoneError ? 'error' : ''}
                          showSearch
                          suffixIcon={
                            <img 
                              src="/image/顶导箭头.png" 
                              alt="arrow" 
                              className="application-form-country-select-arrow"
                            />
                          }
                          onDropdownVisibleChange={(open) => {
                            setIsCountrySelectOpen(open);
                          }}
                          filterOption={(input, option) => {
                            // 只搜索区号
                            const searchText = input.toLowerCase().trim();
                            if (!searchText) return true;
                            
                            const code = option.value || '';
                            const codeWithoutPlus = code.replace('+', '');
                            
                            // 搜索区号（支持输入 +86 或 86）
                            if (code.toLowerCase().includes(searchText) || codeWithoutPlus.includes(searchText)) {
                              return true;
                            }
                            
                            return false;
                          }}
                          getPopupContainer={(triggerNode) => {
                            return overlayRef.current || document.body;
                          }}
                        >
                          {uniqueCountryCodes.map((code) => (
                            <Select.Option key={code} value={code}>
                              {code}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="phone"
                        noStyle
                      >
                        <Input
                          type="tel"
                          className="application-form-phone-input"
                          placeholder={t('applicationForm.fields.phone.placeholder')}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={() => {
                            // 如果已经尝试提交过，实时校验
                            if (hasAttemptedSubmit) {
                              form.validateFields(['phone']).catch(() => {});
                            }
                          }}
                        />
                      </Form.Item>
                    </Input.Group>
                  );
                }}
              </Form.Item>
            </Form.Item>

            {/* 大学名称 */}
            <Form.Item
              name="university"
              label={<span className="application-form-label">{t('applicationForm.fields.university.label')}</span>}
              rules={[{ required: true, message: t('applicationForm.fields.university.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.university.hint')}</p>}
            >
              <Input
                className="application-form-input"
                placeholder={t('applicationForm.fields.university.placeholder')}
              />
            </Form.Item>

            {/* 研究项目名称 */}
            <Form.Item
              name="researchProject"
              label={<span className="application-form-label">{t('applicationForm.fields.researchProject.label')}</span>}
              rules={[{ required: true, message: t('applicationForm.fields.researchProject.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.researchProject.hint')}</p>}
            >
              <Input
                className="application-form-input"
                placeholder={t('applicationForm.fields.researchProject.placeholder')}
              />
            </Form.Item>

            {/* 子女出生日期 */}
            <Form.Item
              name="childBirthDate"
              label={<span className="application-form-label application-form-label2">{t('applicationForm.fields.childBirthDate.label')}</span>}
              rules={[{ required: true, message: t('applicationForm.fields.childBirthDate.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.childBirthDate.hint')}</p>}
            >
              <DatePicker
                className="application-form-input"
                placeholder={t('applicationForm.fields.childBirthDate.placeholder')}
                style={{ width: '100%' }}
                getPopupContainer={(triggerNode) => {
                  return overlayRef.current || document.body;
                }}
                disabledDate={(current) => {
                  // 禁用 2026-01-01 之前的日期
                  return current && current.isBefore(dayjs('2026-01-01'), 'day');
                }}
                onOpenChange={(open) => {
                  // 如果已经尝试提交过，当日期选择器关闭时触发校验
                  if (!open && hasAttemptedSubmit) {
                    form.validateFields(['childBirthDate']).catch(() => {});
                  }
                }}
              />
            </Form.Item>

            {/* 上传学生证 */}
            <Form.Item
              name="studentIdFile"
              label={<span className="application-form-label application-form-label2">{t('applicationForm.fields.studentId.label')}</span>}
              rules={[
                { required: true, message: t('applicationForm.fields.studentId.required') },
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }
                    
                    // 验证所有文件
                    for (const fileItem of value) {
                      const file = fileItem?.originFileObj || fileItem;
                      if (!file) {
                        continue;
                      }
                      
                      // 验证文件格式
                      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type) ||
                        /\.(jpg|jpeg|png|pdf)$/i.test(file.name);
                      if (!isValidType) {
                        return Promise.reject(new Error(t('applicationForm.file.invalidType')));
                      }
                      
                      // 验证文件大小（5MB）
                      const isLt5M = file.size / 1024 / 1024 < 5;
                      if (!isLt5M) {
                        return Promise.reject(new Error(t('applicationForm.file.tooLarge')));
                      }
                    }
                    
                    return Promise.resolve();
                  }
                }
              ]}
              className="application-form-field"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                // 确保返回文件列表
                const fileList = e?.fileList || [];
                return fileList;
              }}
              extra={
                <>
                  <p className="application-form-hint">{t('applicationForm.fields.maxFiles')}</p>
                  <p className="application-form-hint">{t('applicationForm.fields.studentId.hint')}</p>
                </>
              }
            >
              <Upload
                accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                maxCount={10}
                className="application-form-upload"
                beforeUpload={beforeUpload}
                itemRender={customItemRender}
                customRequest={({ file, onSuccess, onError }) => {
                  // 模拟上传成功，立即调用 onSuccess
                  // 注意：这里只是模拟，实际应该调用真实的上传接口
                  // 文件会保存在表单状态中，提交时一起上传
                  try {
                    setTimeout(() => {
                      // onSuccess 的第一个参数是响应对象，第二个参数是文件对象
                      onSuccess({ status: 'ok' }, file);
                    }, 0);
                  } catch (error) {
                    onError(error);
                  }
                }}
                onChange={(info) => {
                  // 文件列表变化时，getValueFromEvent 会自动更新表单值
                  // 这里手动触发保存，确保 onValuesChange 被调用
                  setTimeout(() => {
                    saveFormData();
                  }, 0);
                }}
              >
                {customUploadButton(
                  t('applicationForm.fields.studentId.uploadText1'),
                  t('applicationForm.fields.studentId.uploadText2'),
                  t('applicationForm.fields.studentId.uploadText3')
                )}
              </Upload>
            </Form.Item>

            {/* 上传学校在读单/在读证明 */}
            <Form.Item
              name="enrollmentCertFile"
              label={<span className="application-form-label application-form-label2">{t('applicationForm.fields.enrollmentCert.label')}</span>}
              rules={[
                { required: true, message: t('applicationForm.fields.enrollmentCert.required') },
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }
                    
                    // 验证所有文件
                    for (const fileItem of value) {
                      const file = fileItem?.originFileObj || fileItem;
                      if (!file) {
                        continue;
                      }
                      
                      // 验证文件格式
                      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type) ||
                        /\.(jpg|jpeg|png|pdf)$/i.test(file.name);
                      if (!isValidType) {
                        return Promise.reject(new Error(t('applicationForm.file.invalidType')));
                      }
                      
                      // 验证文件大小（5MB）
                      const isLt5M = file.size / 1024 / 1024 < 5;
                      if (!isLt5M) {
                        return Promise.reject(new Error(t('applicationForm.file.tooLarge')));
                      }
                    }
                    
                    return Promise.resolve();
                  }
                }
              ]}
              className="application-form-field"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                // 确保返回文件列表
                const fileList = e?.fileList || [];
                return fileList;
              }}
              extra={
                <>
                  <p className="application-form-hint">{t('applicationForm.fields.maxFiles')}</p>
                  <p className="application-form-hint">{t('applicationForm.fields.enrollmentCert.hint')}</p>
                </>
              }
            >
              <Upload
                accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                maxCount={10}
                className="application-form-upload"
                beforeUpload={beforeUpload}
                itemRender={customItemRender}
                customRequest={({ file, onSuccess, onError }) => {
                  // 模拟上传成功，立即调用 onSuccess
                  // 注意：这里只是模拟，实际应该调用真实的上传接口
                  // 文件会保存在表单状态中，提交时一起上传
                  try {
                    setTimeout(() => {
                      // onSuccess 的第一个参数是响应对象，第二个参数是文件对象
                      onSuccess({ status: 'ok' }, file);
                    }, 0);
                  } catch (error) {
                    onError(error);
                  }
                }}
                onChange={(info) => {
                  // 文件列表变化时，getValueFromEvent 会自动更新表单值
                  // 这里手动触发保存，确保 onValuesChange 被调用
                  setTimeout(() => {
                    saveFormData();
                  }, 0);
                }}
              >
                {customUploadButton(
                  t('applicationForm.fields.enrollmentCert.uploadText1'),
                  t('applicationForm.fields.enrollmentCert.uploadText2'),
                  t('applicationForm.fields.enrollmentCert.uploadText3')
                )}
              </Upload>
            </Form.Item>

            {/* 上传子女出生证明 */}
            <Form.Item
              name="birthCertFile"
              label={<span className="application-form-label application-form-label2">{t('applicationForm.fields.birthCert.label')}</span>}
              rules={[
                { required: true, message: t('applicationForm.fields.birthCert.required') },
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }
                    
                    // 验证所有文件
                    for (const fileItem of value) {
                      const file = fileItem?.originFileObj || fileItem;
                      if (!file) {
                        continue;
                      }
                      
                      // 验证文件格式
                      const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type) ||
                        /\.(jpg|jpeg|png|pdf)$/i.test(file.name);
                      if (!isValidType) {
                        return Promise.reject(new Error(t('applicationForm.file.invalidType')));
                      }
                      
                      // 验证文件大小（5MB）
                      const isLt5M = file.size / 1024 / 1024 < 5;
                      if (!isLt5M) {
                        return Promise.reject(new Error(t('applicationForm.file.tooLarge')));
                      }
                    }
                    
                    return Promise.resolve();
                  }
                }
              ]}
              className="application-form-field"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                // 确保返回文件列表
                const fileList = e?.fileList || [];
                return fileList;
              }}
              extra={
                <>
                  <p className="application-form-hint">{t('applicationForm.fields.maxFiles')}</p>
                  <p className="application-form-hint">{t('applicationForm.fields.birthCert.hint')}</p>
                </>
              }
            >
              <Upload
                accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                maxCount={10}
                className="application-form-upload"
                beforeUpload={beforeUpload}
                itemRender={customItemRender}
                customRequest={({ file, onSuccess, onError }) => {
                  // 模拟上传成功，立即调用 onSuccess
                  // 注意：这里只是模拟，实际应该调用真实的上传接口
                  // 文件会保存在表单状态中，提交时一起上传
                  try {
                    setTimeout(() => {
                      // onSuccess 的第一个参数是响应对象，第二个参数是文件对象
                      onSuccess({ status: 'ok' }, file);
                    }, 0);
                  } catch (error) {
                    onError(error);
                  }
                }}
                onChange={(info) => {
                  // 文件列表变化时，getValueFromEvent 会自动更新表单值
                  // 这里手动触发保存，确保 onValuesChange 被调用
                  setTimeout(() => {
                    saveFormData();
                  }, 0);
                }}
              >
                {customUploadButton(
                  t('applicationForm.fields.birthCert.uploadText1'),
                  t('applicationForm.fields.birthCert.uploadText2'),
                  t('applicationForm.fields.birthCert.uploadText3')
                )}
              </Upload>
            </Form.Item>

            
            

            {/* 银行名称 */}
            <Form.Item
              name="bankName"
              label={<span className="application-form-label application-form-label2">{t('applicationForm.fields.bankInfo.title')}</span>}
              rules={[{ required: true, message: t('applicationForm.fields.bankInfo.bankName.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.bankInfo.bankName.hint')}</p>}
            >
              <Input
                className="application-form-input"
                placeholder={t('applicationForm.fields.bankInfo.bankName.placeholder')}
              />
            </Form.Item>

            {/* 银行户名 */}
            <Form.Item
              name="accountHolder"
              rules={[{ required: true, message: t('applicationForm.fields.bankInfo.accountHolder.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.bankInfo.accountHolder.hint')}</p>}
            >
              <Input
                className="application-form-input"
                placeholder={t('applicationForm.fields.bankInfo.accountHolder.placeholder')}
              />
            </Form.Item>

            {/* 银行账户号码 */}
            <Form.Item
              name="accountNumber"
              rules={[{ required: true, message: t('applicationForm.fields.bankInfo.accountNumber.required') }]}
              className="application-form-field"
              extra={<p className="application-form-hint">{t('applicationForm.fields.bankInfo.accountNumber.hint')}</p>}
            >
              <Input
                className="application-form-input"
                placeholder={t('applicationForm.fields.bankInfo.accountNumber.placeholder')}
              />
            </Form.Item>

            {/* 访谈意愿 */}
            <Form.Item
              name="interviewWillingness"
              label={<span className="application-form-label">{t('applicationForm.fields.interviewWillingness.label')}</span>}
              rules={[{ required: true, message: t('applicationForm.fields.interviewWillingness.required') }]}
              className="application-form-field application-form-radio-wrapper"
            >
              <div className="application-form-radio-field">
                <p className="application-form-hint" style={{ paddingTop: '0px', marginBottom: '6px' }}>
                  {t('applicationForm.fields.interviewWillingness.description')}
                </p>
                <Radio.Group className="application-form-radio-group" defaultValue="agree">
                  <Radio value="agree" className="application-form-radio">
                    {t('applicationForm.fields.interviewWillingness.agree')}
                  </Radio>
                  <Radio value="disagree" className="application-form-radio">
                    {t('applicationForm.fields.interviewWillingness.disagree')}
                  </Radio>
                </Radio.Group>
              </div>
            </Form.Item>

            {/* 确认声明 */}
            <Form.Item
              name="agreeTerms"
              valuePropName="checked"
              rules={[{ required: true, message: t('applicationForm.fields.agreeTerms.required') }]}
              className="application-form-field application-form-checkbox-field"
            >
              <Checkbox className="application-form-checkbox-label">
                <div className="application-form-checkbox-content">
                  <span className="application-form-checkbox-text">{t('applicationForm.fields.agreeTerms.label')}</span>
                  <p className="application-form-hint">{t('applicationForm.fields.agreeTerms.hint')}</p>
                </div>
              </Checkbox>
            </Form.Item>

            {/* 个人数据使用授权 */}
            <Form.Item
              name="agreePrivacy"
              valuePropName="checked"
              rules={[{ required: true, message: t('applicationForm.fields.agreePrivacy.required') }]}
              className="application-form-field application-form-checkbox-field"
            >
              <Checkbox className="application-form-checkbox-label">
                <div className="application-form-checkbox-content">
                  <span className="application-form-checkbox-text">{t('applicationForm.fields.agreePrivacy.label')}</span>
                  <p className="application-form-hint">
                    {(() => {
                      const hintText = t('applicationForm.fields.agreePrivacy.hint');
                      // 根据不同语言查找隐私政策关键词
                      const privacyKeywords = ['隐私政策', '隱私政策', 'Privacy Policy'];
                      let privacyKeyword = '';
                      for (const keyword of privacyKeywords) {
                        if (hintText.includes(keyword)) {
                          privacyKeyword = keyword;
                          break;
                        }
                      }
                      
                      if (privacyKeyword) {
                        const parts = hintText.split(privacyKeyword);
                        return (
                          <>
                            {parts[0]}
                            <Link 
                              to="/privacy-policy" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="application-form-privacy-link"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              {privacyKeyword}
                            </Link>
                            {parts[1]}
                          </>
                        );
                      }
                      return hintText;
                    })()}
                  </p>
                </div>
              </Checkbox>
            </Form.Item>
          </Form>
        </div>

        {/* 固定底部的提交按钮 */}
        <div className={`application-form-footer ${showFooterShadow ? 'has-shadow' : ''}`}>
          <div className={`application-form-submit-wrapper ${isSubmitting ? 'is-submitting' : ''}`}>
            <ArrowButton 
              text={isSubmitting ? t('applicationForm.submitting') : t('applicationForm.submitButton')}
              variant="special"
              size="large"
              disabled={isSubmitting || !(agreeTerms && agreePrivacy)}
              onClick={() => form.submit()}
            />
          </div>
        </div>
      </div>

      {/* 提交成功弹窗 */}
      <Modal
        open={showSuccessModal}
        onCancel={handleSuccessConfirm}
        footer={null}
        closable={true}
        maskClosable={false}
        centered
        width={480}
        className="application-form-result-modal"
      >
        <div className="application-form-result-content">
          <div className="application-form-result-icon success">
            <CheckCircleOutlined />
          </div>
          <h3 className="application-form-result-title">
            {t('applicationForm.submitSuccess')}
          </h3>
          <p className="application-form-result-message">
            {t('applicationForm.submitSuccess.message')}
          </p>
          <div className="application-form-result-actions">
            <button 
              className="application-form-result-button primary"
              onClick={handleSuccessConfirm}
            >
              {t('applicationForm.submitSuccess.button')}
            </button>
          </div>
        </div>
      </Modal>

      {/* 提交失败弹窗 */}
      <Modal
        open={showErrorModal}
        onCancel={handleErrorBack}
        footer={null}
        closable={true}
        maskClosable={false}
        centered
        width={480}
        className="application-form-result-modal"
      >
        <div className="application-form-result-content">
          <div className="application-form-result-icon error">
            <CloseCircleOutlined />
          </div>
          <h3 className="application-form-result-title">
            {t('applicationForm.submitError')}
          </h3>
          <p className="application-form-result-message">
            {t('applicationForm.submitError.message')}
          </p>
          <div className="application-form-result-actions">
            <button 
              className="application-form-result-button secondary"
              onClick={handleErrorBack}
            >
              {t('applicationForm.submitError.back')}
            </button>
            <button 
              className="application-form-result-button primary"
              onClick={handleErrorRetry}
              disabled={isSubmitting}
            >
              {t('applicationForm.submitError.retry')}
            </button>
          </div>
        </div>
      </Modal>

      {/* 文件预览全屏组件 */}
      {previewVisible && previewFile && (
        <div className="application-form-file-preview-overlay">
          <div className="application-form-file-preview-header">
            <button 
              className="application-form-file-preview-back"
              onClick={handlePreviewClose}
            >
              <ArrowLeftOutlined />
            </button>
            <span className="application-form-file-preview-title">{previewFile.name}</span>
          </div>
          <div className="application-form-file-preview-content">
            {previewFile.type.startsWith('image/') ? (
              <img 
                src={previewFile.url} 
                alt={previewFile.name}
                className="application-form-file-preview-image"
              />
            ) : previewFile.type === 'application/pdf' || previewFile.name.toLowerCase().endsWith('.pdf') ? (
              <iframe 
                src={previewFile.url}
                title={previewFile.name}
                className="application-form-file-preview-pdf"
              />
            ) : (
              <div className="application-form-file-preview-unsupported">
                <p>{t('applicationForm.file.unsupportedPreview')}</p>
                <p className="application-form-file-preview-filename">{previewFile.name}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicationFormModal;

